import React, { useState, useEffect } from 'react';
import { Button } from '../shadComponents/ui/button';
import { Label } from '../shadComponents/ui/label';
import useAuthenticatedApi from '../Authentication/components/hooks/useAuthenticatedApi';
import ReferencePhotoUploader from './ReferencePhotoUploader';

interface ReferencePhoto {
    id: number;
    photoUrl: string;
    faceId: string | null;
    createdAt: string;
}

interface Props {
    userId: number;
}

const ReferencePhotoManager: React.FC<Props> = ({ userId }) => {
    const [photos, setPhotos] = useState<ReferencePhoto[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const authenticatedApi = useAuthenticatedApi();

    useEffect(() => {
        fetchPhotos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const fetchPhotos = async () => {
        try {
            const response = await authenticatedApi.get(
                `/gallery-users/${userId}/reference-photos`,
            );
            const data = response.data;
            setPhotos(data);
        } catch (error) {
            console.error('Error fetching reference photos:', error);
        }
    };

    const handleUpload = async (file: File) => {
        setIsUploading(true);
        const formData = new FormData();
        formData.append('photo', file);

        try {
            const response = await authenticatedApi.post(
                `/gallery-users/${userId}/reference-photo`,
                formData,
            );

            if (response.status !== 201) {
                throw new Error('Failed to upload photo');
            }

            fetchPhotos();
        } catch (error) {
            console.error('Error uploading photo:', error);
            // TODO: Add proper error handling/notification
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async (photoId: number) => {
        if (
            !window.confirm(
                'Are you sure you want to delete this reference photo?',
            )
        )
            return;

        try {
            const response = await authenticatedApi.delete(
                `/gallery-users/reference-photo/${photoId}`,
            );

            if (response.status !== 204) {
                throw new Error('Failed to delete photo');
            }

            fetchPhotos();
        } catch (error) {
            console.error('Error deleting photo:', error);
            // TODO: Add proper error handling/notification
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <Label>Reference Photos</Label>
            </div>

            <ReferencePhotoUploader
                onUpload={handleUpload}
                isUploading={isUploading}
            />

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {photos.map((photo) => (
                    <div key={photo.id} className="relative group">
                        {/* eslint-disable-next-line */}
                        <img
                            src={photo.photoUrl}
                            alt="Reference photo"
                            loading="lazy"
                            className="w-full h-48 object-cover rounded"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center">
                            <Button
                                variant="destructive"
                                size="sm"
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(
                                    e: React.MouseEvent<HTMLButtonElement>,
                                ) => {
                                    e.preventDefault();
                                    handleDelete(photo.id);
                                }}
                            >
                                Delete
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ReferencePhotoManager;
